import { useMemo, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { EventInput, EventClickArg } from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';
import { BOOKING_STATUS_COLORS, STAFF_COLORS } from '@/types/calendar';

export interface CalendarBookingWithStaff {
  id: string;
  businessId: string;
  businessName?: string;
  serviceId: string;
  serviceName: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  scheduledAt: string;
  endsAt: string;
  status: string;
  paymentStatus?: string;
  notes?: string;
  staffId?: string;
  staffName?: string;
}

export interface SerializedAvailabilityBlock {
  id: string;
  staffId: string;
  staffName: string;
  start: string;
  end: string;
  type: string;
  title?: string;
  backgroundColor: string;
}

interface FullCalendarWrapperProps {
  bookings: CalendarBookingWithStaff[];
  onBookingClick: (bookingId: string) => void;
  onDateClick: (date: Date) => void;
  staff: any[];
  selectedStaffIds: string[];
  showStaffName: boolean;
  colorMode: string;
  viewMode: string;
  availabilityBlocks: SerializedAvailabilityBlock[];
  showAvailability: boolean;
  onAvailabilityClick: (block: SerializedAvailabilityBlock) => void;
}

export default function FullCalendarWrapper({
  bookings,
  onBookingClick,
  onDateClick,
  staff,
  selectedStaffIds,
  showStaffName,
  colorMode,
  viewMode,
  availabilityBlocks,
  showAvailability,
  onAvailabilityClick,
}: FullCalendarWrapperProps) {
  const calendarRef = useRef<FullCalendar>(null);

  const events: EventInput[] = useMemo(() => {
    const bookingEvents: EventInput[] = bookings.map((booking) => {
      let backgroundColor: string;

      if (colorMode === 'staff' && booking.staffId) {
        const staffIndex = staff.findIndex((s) => s.id === booking.staffId);
        const staffColor = staffIndex >= 0 ? STAFF_COLORS[staffIndex % STAFF_COLORS.length] : STAFF_COLORS[0];
        backgroundColor = staffColor.primary;
      } else {
        const statusColors = BOOKING_STATUS_COLORS[booking.status as keyof typeof BOOKING_STATUS_COLORS];
        backgroundColor = statusColors?.hex || '#64748b';
      }

      return {
        id: booking.id,
        title: `${booking.customerName} - ${booking.serviceName}`,
        start: booking.scheduledAt,
        end: booking.endsAt,
        backgroundColor,
        borderColor: backgroundColor,
        extendedProps: {
          bookingId: booking.id,
        },
      };
    });

    const availabilityEvents: EventInput[] = showAvailability
      ? availabilityBlocks.map((block) => ({
          id: block.id,
          title: block.title || block.staffName,
          start: block.start,
          end: block.end,
          backgroundColor: block.backgroundColor,
          borderColor: block.backgroundColor,
          display: 'background',
          extendedProps: {
            availabilityBlock: block,
          },
        }))
      : [];

    return [...bookingEvents, ...availabilityEvents];
  }, [bookings, staff, colorMode, showAvailability, availabilityBlocks]);

  const handleEventClick = (info: EventClickArg) => {
    info.jsEvent.preventDefault();

    if (info.event.extendedProps.bookingId) {
      onBookingClick(info.event.extendedProps.bookingId);
    } else if (info.event.extendedProps.availabilityBlock) {
      onAvailabilityClick(info.event.extendedProps.availabilityBlock);
    }
  };

  const handleDateClick = (info: DateClickArg) => {
    onDateClick(info.date);
  };

  return (
    <div className="w-full min-h-[600px]">
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        events={events}
        eventClick={handleEventClick}
        dateClick={handleDateClick}
        slotMinTime="08:00:00"
        slotMaxTime="20:00:00"
        nowIndicator={true}
        weekends={true}
        height="auto"
        editable={false}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={true}
        allDaySlot={false}
      />
    </div>
  );
}
